import React from 'react'
import {
  AlignItems,
  Body2,
  Box,
  Col,
  Color,
  Display,
  Grid,
  Heading2,
  Heading3,
  Heading4,
  Icon,
  IconVariant,
  JustifyContent,
  Link,
  LinkVariant,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { ContentstackImage } from '../ContentstackImage'
import { getRenderableRichTextBlocks, RichText } from '../../components/RichText'
import { CtaLink, mapCmsCta } from '../../cta'
import type { OnTriggerPromoCode } from '../../cta'

import type { RichTextContent } from '../../types/contentstack'
import type { ImageTextColumnsFields } from './types'

export type ImageTextColumnsProps = ImageTextColumnsFields & {
  onTriggerPromocode?: OnTriggerPromoCode
}

type OrderedColProps = React.ComponentProps<typeof Col> & {
  mobileOrder: number
}

function BaseOrderedCol({ mobileOrder: _mobileOrder, ...props }: OrderedColProps) {
  return <Col {...props} />
}

const OrderedCol = withCitrusTheme(BaseOrderedCol, (theme, props: OrderedColProps) => {
  const breakpointToLarge = `@media (max-width: ${theme.tokens.breakpoints[2]})`
  return {
    [breakpointToLarge]: { order: props.mobileOrder },
  }
})

// Inline links in CMS rich text. Citrus's default Link variant renders its own
// box-shadow underline plus AxiformaBold 16→18px typography; combined with an
// added text-decoration that read as a double underline and made links larger
// and bolder than the surrounding paragraph. LinkVariant.None sheds that
// treatment so the link inherits the paragraph's type and carries a single,
// explicit underline. Shared by the intro body and the sub-section body.
const StyledRichTextLink = withCitrusTheme(Link, (theme) => ({
  color: theme.color(Color.Secondary_400),
  textDecoration: 'underline',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  fontFamily: 'inherit',
}))

function RichTextLink({
  href,
  target,
  children,
}: {
  href: string
  target?: string
  children: React.ReactNode
}) {
  return (
    <StyledRichTextLink variant={LinkVariant.None} href={href} target={target}>
      {children}
    </StyledRichTextLink>
  )
}

function renderRichTextLink({
  children,
  href,
  target,
  key,
}: {
  children: React.ReactNode
  href: string
  target?: string
  key: React.Key
}) {
  return (
    <RichTextLink key={key} href={href} target={target}>
      {children}
    </RichTextLink>
  )
}

// Bare list markup carries no class, so it inherits the legacy global body reset
// colour instead of the design-system body grey the paragraphs use. Only the
// colour is off — font size/family/weight already match — so set just the colour
// token on the list item (covers both the bullet and the text). ColdGrey_600 is
// the DS body grey (#516272) Body2 paragraphs render in.
const RichTextListItem = withCitrusTheme('li', (theme) => ({
  color: theme.color(Color.ColdGrey_600),
}))

function renderRichTextListItem({ children, key }: { children: React.ReactNode; key: React.Key }) {
  return <RichTextListItem key={key}>{children}</RichTextListItem>
}

// Sub Body is tolerant of both shapes during the text→rich-text migration: a
// legacy plain string renders as a Body2 paragraph, while rich-text content
// renders through RichText (still Body2 paragraphs) so inline bold/italic/
// underline/links work — shown as a separate paragraph under the sub heading.
function SubBody({ value }: { value?: string | RichTextContent }) {
  if (!value) {
    return null
  }

  if (typeof value === 'string') {
    return <Body2 size={4}>{value}</Body2>
  }

  return (
    <RichText
      content={value}
      paragraphGapSize={2}
      renderParagraph={({ children, key }) => (
        <Body2 key={key} size={4} style={{ whiteSpace: 'pre-wrap' }}>
          {children}
        </Body2>
      )}
      renderLink={renderRichTextLink}
      renderListItem={renderRichTextListItem}
    />
  )
}

function SectionHeading({ level, children }: { level: 'h2' | 'h3'; children: React.ReactNode }) {
  if (level === 'h2') {
    return <Heading2>{children}</Heading2>
  }
  return <Heading3>{children}</Heading3>
}

export function ImageTextColumns({
  heading,
  heading_level,
  image_position,
  image,
  image_mobile,
  image_alt,
  body,
  sub_sections,
  closing_body,
  cta,
  onTriggerPromocode,
}: ImageTextColumnsProps) {
  const ctaLink = mapCmsCta(cta, onTriggerPromocode)
  const imageOnLeft = image_position === 'left'
  const hasSubSections = sub_sections && sub_sections.length > 0
  const shouldAddSubSectionCtaSpace = ctaLink && hasSubSections && !closing_body
  const textMobileOrder = 1
  const imageMobileOrder = 2

  const TextColumn = (
    <OrderedCol size={[12, null, null, 6]} mobileOrder={textMobileOrder}>
      <Box paddingRight={imageOnLeft ? 0 : [0, null, null, 10]}>
        <SectionHeading level={heading_level}>{heading}</SectionHeading>
        <Space size={2} />
        {body && (
          <Box paddingBottom={6}>
            <RichText
              content={body}
              paragraphGapSize={6}
              renderParagraph={({ children, key }) => (
                <Body2 key={key} size={4} style={{ whiteSpace: 'pre-wrap' }}>
                  {children}
                </Body2>
              )}
              renderLink={renderRichTextLink}
              renderListItem={renderRichTextListItem}
            />
          </Box>
        )}
        {hasSubSections && (
          <Box>
            {sub_sections?.map((item, index) => {
              const metadataUid = (item as { _metadata?: { uid: string } })._metadata?.uid
              const hasRichTextBody = getRenderableRichTextBlocks(item.body).length > 0

              return (
                <Box
                  key={metadataUid ?? item.body?.uid ?? `sub-section-${index}`}
                  display={item.show_tick_icon ? Display.Flex : Display.Block}
                  paddingBottom={item.show_tick_icon ? 2 : 4}
                >
                  {item.show_tick_icon && (
                    <Box paddingRight={1}>
                      <Icon name="tick" variant={IconVariant.Confirmation} data-testid="tick" />
                    </Box>
                  )}
                  <Box>
                    {hasRichTextBody ? (
                      <RichText
                        content={item.body}
                        paragraphGapSize={2}
                        renderParagraph={({ children, key }) => (
                          <Body2 key={key} size={4} style={{ whiteSpace: 'pre-wrap' }}>
                            {children}
                          </Body2>
                        )}
                        renderLink={renderRichTextLink}
                        renderListItem={renderRichTextListItem}
                      />
                    ) : (
                      <>
                        {item.sub_heading && (
                          <Heading4 size={6} color={Color.ColdGrey_800}>
                            {item.sub_heading}
                          </Heading4>
                        )}
                        <SubBody value={item.sub_body} />
                      </>
                    )}
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
        {shouldAddSubSectionCtaSpace && <Space size={6} />}
        {closing_body && (
          <>
            <Space size={2} />
            <Box paddingBottom={6}>
              <Body2 size={4}>{closing_body}</Body2>
            </Box>
          </>
        )}
      </Box>
      {ctaLink && (
        <Box
          display={Display.Flex}
          justifyContent={[JustifyContent.Center, JustifyContent.FlexStart]}
          paddingBottom={[6, null, null, 0]}
        >
          <CtaLink cta={ctaLink} />
        </Box>
      )}
      <Space size={[6, null, null, 0]} />
    </OrderedCol>
  )

  const ImageColumn = (
    <OrderedCol
      size={[12, null, null, 6]}
      mobileOrder={imageMobileOrder}
      display={Display.Flex}
      alignItems={AlignItems.FlexStart}
      justifyContent={JustifyContent.Center}
    >
      <Box
        paddingRight={imageOnLeft ? [0, null, null, 10] : 0}
        maxWidth={[null, null, null, '600px']}
        width="100%"
      >
        <Box style={{ borderRadius: '8px', overflow: 'hidden' }}>
          <ContentstackImage
            alt={image_alt}
            fit="contain"
            sizes="(max-width: 1023px) 100vw, 600px"
            src={image.url}
            mobileSrc={image_mobile?.url}
            mobileMedia="(max-width: 1023px)"
            width="100%"
          />
        </Box>
      </Box>
    </OrderedCol>
  )

  return (
    <Box paddingV={[8, 12, 12, 16]} paddingH={[4, 6, 14]}>
      <Grid>
        {imageOnLeft ? ImageColumn : TextColumn}
        {imageOnLeft ? TextColumn : ImageColumn}
      </Grid>
    </Box>
  )
}
